import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ChatMessageBubble } from "@/components/chat/ChatMessageBubble";
import { MessageComposer } from "@/components/chat/MessageComposer";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { fetchDraftCopilotHistory, requestDraftCopilot } from "@/lib/api/drafts";
import { cn } from "@/lib/utils";
import type { ChatMessage } from "@/types";
import type { DraftCopilotHistoryMessage, DraftCopilotResponse } from "@/types/drafting";

interface DraftCopilotChatProps {
  matterId: string;
  onContentUpdated?: (contentJson?: unknown, versionId?: string) => void;
  className?: string;
}

interface CopilotChatMessage extends ChatMessage {
  copilotResult?: DraftCopilotResponse;
  sourceInstruction?: string;
}

const WELCOME_MESSAGE: CopilotChatMessage = {
  id: "draft-copilot-welcome",
  role: "assistant",
  parts: [
    {
      type: "text",
      text:
        "Draft assembled from your background notes and attached source materials. Ask me to refine clauses, adjust tone, or apply policy from your playbook.",
    },
  ],
  createdAt: new Date().toISOString(),
};

function historyRowToChatMessage(row: DraftCopilotHistoryMessage): CopilotChatMessage {
  const copilot = row.metadata?.copilot;
  const sourceInstruction = row.metadata?.source_instruction;

  if (row.role === "user") {
    return {
      id: row.id,
      role: "user",
      parts: [{ type: "text", text: row.content }],
      createdAt: row.created_at,
    };
  }

  const message: CopilotChatMessage = {
    id: row.id,
    role: "assistant",
    parts: [{ type: "text", text: row.content }],
    createdAt: row.created_at,
    ...(sourceInstruction ? { sourceInstruction } : {}),
  };

  if (copilot) {
    message.copilotResult = {
      mode: copilot.mode,
      applied: copilot.applied,
      version_id: copilot.version_id,
      proposal: {
        explanation: copilot.explanation ?? row.content.split("\n\n")[0] ?? row.content,
        target_section_key: copilot.target_section_key ?? "",
      },
    };
  }

  return message;
}

export function DraftCopilotChat({ matterId, onContentUpdated, className }: DraftCopilotChatProps) {
  const { user, isLoading: isAuthLoading } = useAuth();
  const { showToast } = useToast();
  const [messages, setMessages] = useState<CopilotChatMessage[]>([WELCOME_MESSAGE]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, []);

  const loadChatHistory = useCallback(async () => {
    if (!user) return [];
    setHistoryError(null);
    try {
      const rows = await fetchDraftCopilotHistory(matterId);
      const chatMessages = rows.map(historyRowToChatMessage);
      setMessages(chatMessages.length > 0 ? chatMessages : [WELCOME_MESSAGE]);
      return chatMessages;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load copilot history";
      setHistoryError(message);
      console.error("Error loading draft copilot history:", err);
      return [];
    }
  }, [matterId, user]);

  useEffect(() => {
    if (isAuthLoading) return;

    if (!user) {
      setIsLoadingHistory(false);
      return;
    }

    let cancelled = false;
    setIsLoadingHistory(true);
    void loadChatHistory().finally(() => {
      if (!cancelled) setIsLoadingHistory(false);
    });

    return () => {
      cancelled = true;
    };
  }, [isAuthLoading, loadChatHistory, user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, scrollToBottom]);

  async function sendInstruction(instruction: string, apply: boolean, messageId?: string) {
    setIsLoading(true);
    setError(null);
    try {
      const result = await requestDraftCopilot(matterId, instruction, apply, messageId);
      await loadChatHistory();
      if (result.applied) {
        showToast("Copilot changes applied to the draft.", "success");
        onContentUpdated?.(result.content_json, result.version_id);
      }
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Copilot request failed";
      setError(message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const instruction = input.trim();
    if (!instruction || isLoading) return;

    const userMessage: CopilotChatMessage = {
      id: `draft-copilot-pending-${Date.now()}`,
      role: "user",
      parts: [{ type: "text", text: instruction }],
      createdAt: new Date().toISOString(),
    };

    setMessages((prev) => (prev[0]?.id === WELCOME_MESSAGE.id ? [userMessage] : [...prev, userMessage]));
    setInput("");
    await sendInstruction(instruction, true);
  }

  function resolveSourceInstruction(message: CopilotChatMessage): string | undefined {
    const fromMetadata = message.sourceInstruction?.trim();
    if (fromMetadata) return fromMetadata;

    const index = messages.findIndex((item) => item.id === message.id);
    if (index <= 0) return undefined;

    for (let i = index - 1; i >= 0; i -= 1) {
      const candidate = messages[i];
      if (candidate.role !== "user") continue;
      const text = candidate.parts.find((part) => part.type === "text")?.text?.trim();
      if (text) return text;
    }

    return undefined;
  }

  async function handleApplyProposal(message: CopilotChatMessage) {
    const instruction = resolveSourceInstruction(message);
    if (!instruction) {
      setError("Could not find the original instruction for this proposal.");
      return;
    }

    await sendInstruction(instruction, true, message.id);
  }

  const showWelcomeOnly =
    !isLoadingHistory && messages.length === 1 && messages[0]?.id === WELCOME_MESSAGE.id;

  return (
    <div
      className={cn(
        "flex h-full min-h-0 flex-col overflow-hidden bg-[var(--chat-shell-bg)] dark:bg-gradient-to-b dark:from-slate-800 dark:to-slate-900",
        className
      )}
    >
      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-none">
        <div className="space-y-4 p-4">
          {isLoadingHistory ? (
            <div className="flex items-center gap-2 text-sm text-[var(--lm-muted)] dark:text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading copilot history...
            </div>
          ) : null}
          {historyError ? (
            <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-200">
              {historyError}
            </p>
          ) : null}
          {showWelcomeOnly ? <ChatMessageBubble message={WELCOME_MESSAGE} /> : null}
          {(showWelcomeOnly ? [] : messages).map((message) => (
            <div key={message.id} className="space-y-2">
              <ChatMessageBubble message={message} />
              {message.role === "assistant" &&
              message.copilotResult &&
              !message.copilotResult.applied ? (
                <div className="pl-10">
                  <Button
                    type="button"
                    size="xs"
                    variant="default"
                    disabled={isLoading}
                    onClick={() => void handleApplyProposal(message)}
                  >
                    Apply proposal
                  </Button>
                </div>
              ) : null}
            </div>
          ))}
          {isLoading ? (
            <div className="flex items-center gap-2 pl-10 text-sm text-[var(--lm-muted)] dark:text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Copilot is thinking...
            </div>
          ) : null}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <MessageComposer
        value={input}
        onChange={setInput}
        onSubmit={handleSubmit}
        disabled={isLoading}
        canSubmit={Boolean(input.trim())}
        error={error}
        sendTitle="Send instruction"
        sendAriaLabel="Send instruction"
      />
    </div>
  );
}
